/**
 * Rota pública `/proposta/:token` — o lead recebe o link por email ou
 * WhatsApp e abre aqui (PR-M1-01c-ii.b + fluxo dúvida PR-M1-01c-ii.c).
 *
 * Zero autenticação — o token na URL é o segredo. Edge function
 * `read-public-proposal` valida e resolve o conteúdo.
 *
 * Estados:
 *   - loading  → skeleton
 *   - not_found/error → página "link não encontrado"
 *   - accepted → snapshot com banner de confirmação (link continua válido)
 *   - rejected → banner "proposta recusada"
 *   - in_negotiation (dúvida enviada) → banner "sua dúvida foi registrada"
 *   - sent/viewed → ProposalPublicView com botões Aceitar/Dúvida
 *
 * Aceite ainda mostra toast "em breve" — fica para PR-M1-01f (ZapSign).
 */
// design-system:exempt rota pública renderiza ProposalPublicView que já
// encapsula PageContainer/PageIntroCard equivalentes com cabeçalho próprio.
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, PublicShell, Skeleton, toast } from '@kmgestao/ui';

import {
  ContractApprovalSection,
  ContratadaSignatureFooter,
  ProposalPublicView,
  useAcceptProposal,
  usePublicProposal,
} from '../../features/proposals/index.js';

import { ProposalQuestionDialog } from './ProposalQuestionDialog.js';

/** Mensagens de erro do edge runtime do Supabase que indicam falha
 * transitória (cold start, rede instável, infra do provedor). Quando o
 * erro bate com algum desses padrões, mostramos copy diferente — "estamos
 * com instabilidade momentânea, tente novamente" — em vez de "link não
 * encontrado" (que sugere falsamente que o token é inválido). Inclui:
 *   - 503 do gateway Supabase (visto em prod 2026-04-25)
 *   - "Failed to send a request to the Edge Function" do supabase-js
 *   - timeouts de fetch */
function isTransientError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to send a request') ||
    msg.includes('failed to fetch') ||
    msg.includes('503') ||
    msg.includes('502') ||
    msg.includes('504') ||
    msg.includes('network') ||
    msg.includes('timeout')
  );
}

function PublicProposalErrorState({
  error,
  onRetry,
  isRetrying,
}: {
  error: unknown;
  onRetry: () => void;
  isRetrying: boolean;
}) {
  const transient = isTransientError(error);
  return (
    <div className="mx-auto max-w-lg space-y-4 p-8 text-center">
      <h1 className="text-2xl font-semibold text-foreground">
        {transient ? 'Instabilidade momentânea' : 'Link não encontrado'}
      </h1>
      <p className="text-sm text-muted-foreground">
        {transient
          ? 'Houve um problema temporário ao buscar sua proposta. Em geral resolve em poucos segundos. Tente novamente.'
          : 'A proposta pode ter sido removida, estar expirada ou o link pode estar errado.'}
      </p>
      <p className="text-sm text-muted-foreground">
        {transient
          ? 'Se persistir, entre em contato com o consultor.'
          : 'Se você recebeu este link do consultor, entre em contato para solicitar um novo.'}
      </p>
      <div className="flex justify-center pt-2">
        <Button type="button" onClick={onRetry} disabled={isRetrying} size="sm">
          {isRetrying ? 'Tentando novamente…' : 'Tentar novamente'}
        </Button>
      </div>
    </div>
  );
}

export function PublicProposalPage() {
  const { token } = useParams<{ token: string }>();
  const query = usePublicProposal(token);
  const [questionOpen, setQuestionOpen] = useState(false);
  const acceptProposal = useAcceptProposal();

  // "Aceitar e avançar" — PR-M1-01f (phase .a, 2026-04-24).
  // Transita status pra accepted + notifica Karla por email. A
  // integração ZapSign (geração + envio de contrato) fica pro próximo
  // PR (phase .b) quando as credenciais estiverem disponíveis — por
  // ora Karla recebe o email e dispara o contrato manualmente.
  const handleAccept = () => {
    if (!token || acceptProposal.isPending) return;
    acceptProposal.mutate(
      { token },
      {
        onSuccess: () => {
          toast.success('Proposta aceita!', {
            description:
              'Nosso time recebeu a confirmação e vai preparar o contrato. Você receberá um email nas próximas horas.',
          });
          // Query pública já é invalidada pelo hook — o banner "accepted"
          // aparece automaticamente após refetch.
        },
        onError: (err) =>
          toast.error('Falha ao registrar aceite', {
            description: err.message,
          }),
      },
    );
  };
  const handleQuestion = () => setQuestionOpen(true);

  return (
    <PublicShell
      // Brand do topo omitido propositalmente (PR-M1-01c-ii.e-layout):
      // o hero dentro do `ProposalPublicView` já exibe a logomarca
      // prominentemente no centro. Ter a mesma logo duas vezes competia
      // visualmente — especialmente no mobile (reportado pelo Alexandre
      // 2026-04-24). Footer institucional mantido.
      footer={
        <p className="text-xs">KM Gestão · consultoria e gestão para clínicas brasileiras.</p>
      }
      // Libera o `<main>` do shell (max-w-3xl + padding) — o
      // `ProposalPublicView` controla largura internamente via
      // bandas full-width + conteúdo constrained a 4xl (PR-M1-01c-ii.e-layout).
      contentClassName="max-w-none px-0 py-0 sm:px-0 sm:py-0 lg:px-0"
    >
      {query.isLoading ? (
        <div className="mx-auto max-w-3xl space-y-6 p-8">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : query.isError || !query.data ? (
        <PublicProposalErrorState
          error={query.error}
          onRetry={() => void query.refetch()}
          isRetrying={query.isFetching}
        />
      ) : (
        <>
          {/* Banners de status — renderizados fora do ProposalPublicView
              pra ficarem ACIMA do hero. Usam container próprio constrained
              a 4xl pra casar com a largura visual do corpo. */}
          {query.data.proposal.status === 'accepted' ? (
            <div className="w-full bg-accent/10 p-4 sm:px-8">
              <div
                className="mx-auto max-w-4xl rounded-lg border border-accent/40 bg-accent/10 p-4 text-sm text-accent-foreground"
                role="status"
              >
                ✅ Proposta aceita em{' '}
                {query.data.proposal.accepted_at
                  ? new Date(query.data.proposal.accepted_at).toLocaleString('pt-BR')
                  : '—'}
                {query.data.contract
                  ? '. Abaixo está seu contrato — revise e aprove ou solicite ajuste.'
                  : '. Nosso time está preparando seu contrato — você recebe um email quando estiver pronto.'}
              </div>
            </div>
          ) : query.data.proposal.status === 'rejected' ? (
            <div className="w-full bg-destructive/10 p-4 sm:px-8">
              <div
                className="mx-auto max-w-4xl rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
                role="status"
              >
                Esta proposta foi encerrada. Entre em contato com o consultor se quiser reabrir a
                conversa.
              </div>
            </div>
          ) : query.data.proposal.status === 'in_negotiation' &&
            query.data.proposal.questioning_message ? (
            <div className="w-full bg-warning/5 p-4 sm:px-8">
              <div
                className="mx-auto max-w-4xl space-y-2 rounded-lg border border-warning/40 bg-warning/5 p-4 text-sm text-foreground"
                role="status"
              >
                <p className="font-semibold">✉️ Sua dúvida foi registrada</p>
                <p className="text-muted-foreground">
                  Enviada em{' '}
                  {query.data.proposal.questioning_message && query.data.proposal.updated_at
                    ? new Date(query.data.proposal.updated_at).toLocaleString('pt-BR')
                    : ''}
                  . Nossa equipe vai responder pelo seu email em até 1 dia útil. Você pode retornar
                  a esta página a qualquer momento para aceitar ou enviar uma nova mensagem.
                </p>
                <details className="text-sm">
                  <summary className="cursor-pointer font-medium text-foreground">
                    Ver sua mensagem
                  </summary>
                  <p className="mt-2 whitespace-pre-wrap rounded-md bg-background/50 p-3 text-foreground">
                    {query.data.proposal.questioning_message}
                  </p>
                </details>
              </div>
            </div>
          ) : null}

          {/* Quando proposta JÁ FOI ACEITA E o contrato foi gerado, o foco
              do lead muda — agora é só revisar e aprovar o contrato.
              Renderizar a proposta inteira de novo (hero + planos + valores
              + considerações) confunde: o lead aceitou aquilo já, agora
              precisa ver o PDF e clicar Aprovar. Reportado pelo Alexandre
              2026-04-25. Mostra apenas:
                - Banner topo de status (renderizado acima)
                - ContractApprovalSection (PDF + botões Aprovar/Ajustar)
                - ContratadaSignatureFooter (Atenciosamente · Karla · KM Gestão)

              Antes de aceitar (ou se contrato ainda não existe), continua
              o fluxo normal: ProposalPublicView com tudo. */}
          {query.data.proposal.status === 'accepted' && query.data.contract && token ? (
            <>
              <ContractApprovalSection token={token} contract={query.data.contract} />
              <ContratadaSignatureFooter contratada={query.data.platform_settings.contratada} />
            </>
          ) : (
            <ProposalPublicView
              proposal={query.data.proposal}
              platformSettings={query.data.platform_settings}
              lead={query.data.lead}
              mode={
                query.data.proposal.status === 'rejected' ||
                query.data.proposal.status === 'expired'
                  ? 'preview'
                  : 'public'
              }
              onAccept={handleAccept}
              onQuestion={handleQuestion}
              isAcceptPending={acceptProposal.isPending}
            />
          )}

          {token ? (
            <ProposalQuestionDialog
              open={questionOpen}
              onOpenChange={setQuestionOpen}
              token={token}
              leadFirstName={query.data.lead.full_name.split(' ')[0] ?? query.data.lead.full_name}
            />
          ) : null}
        </>
      )}
    </PublicShell>
  );
}
